import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vShow as _vShow, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["textContent"];
const _hoisted_2 = ["disabled"];
const _hoisted_3 = {
    key: 2,
    class: "rec-flyout-indicator"
};
import SvgIcon from "@/common/components/SvgIcon.vue";
import { toCssSizeUnit } from "@/common/helpers/style";
import { vTooltip } from "@/common/directives/tip-directive";
import { onClickOutside, useElementBounding, useScreenOrientation, useVModel, useWindowSize } from "@vueuse/core";
import { v4 as uuid } from "uuid";
import { MenuItemType, useDropdownAccessibility } from "@/common/composables/dropdown-accessibility";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
//Props
export default /*@__PURE__*/ _defineComponent({
    __name: 'DropdownMenu',
    props: {
        absolute: { type: Boolean },
        appendIcon: { default: "" },
        appendIconInside: { default: "" },
        autoClose: { type: Boolean, default: true },
        buttonClass: { default: "" },
        disabled: { type: Boolean },
        error: { default: "" },
        iconSrc: { default: "/svg/svg-defs.svg" },
        menuClass: { default: "" },
        menuMaxHeight: { default: 0 },
        menuPosition: { default: "" },
        menuWidth: { default: 0 },
        menuWidthMatchButton: { type: Boolean },
        modelValue: { type: Boolean },
        offsetY: { default: 3 },
        placement: { default: "auto" },
        prependIcon: { default: "" },
        prependIconInside: { default: "" },
        buttonOnly: { type: Boolean },
        type: { default: "custom" },
        useVShow: { type: Boolean },
        windowPadding: { default: 20 },
        ignoreClickOutside: { default: () => [] },
        active: { type: Boolean },
        tooltip: { default: "" },
        align: { default: undefined },
        lockPositionOnOpen: { type: Boolean, default: false }
    },
    emits: ["update:modelValue", "click"],
    setup(__props, { expose: __expose, emit: __emit }) {
        /********************************************
         * DropdownMenu Component
         * Provides a clickable trigger and a positioned menu.
         *
         * Pass in two slots for the menu content
         *  <template #button></template>
         *  <template #menu></template>
         *
         * Props:
         *  absolute: Controls whether the menu is positioned absolutely (default: fixed positioning). Can be used to fix issues with menus inside scrollable containers
         *  appendIcon: Icon to be appended to the button
         *  appendIconInside: Icon to be appended to the button text
         *  autoClose: Controls whether the menu automatically closes when an item is clicked. To handle this selectively, prevent the propogation of click events
         *  buttonClass: Classes to be applied to the button element
         *  disabled: Disables the button
         *  error: Error message to be displayed
         *  iconSrc: Path to the svg icon sprite
         *  ignoreClickOutside: Ignore clicks outside the menu on the specified element (valid css selector)
         *  menuClass: Classes to be applied to the menu element
         *  menuMaxHeight: Maximum height of the menu
         *  menuPosition: Controls the position of the menu relative to the button
         *  menuWidth: Width of the menu
         *  modelValue: Controls the open state of the menu
         *  offsetY: Vertical offset of the menu from the button
         *  placement: Controls the vertical placement of the menu. Options are "above", "below", and "auto" (default)
         *  prependIcon: Icon to be prepended to the button
         *  prependIconInside: Icon to be prepended to the button text
         *  buttonOnly: Controls whether the menu is in static mode
         *  type: Controls the type of dropdown menu. Options are "button", "flyout", "select", and "custom"
         *  useVShow: Controls whether the menu is shown using v-show or v-if
         *  windowPadding: Padding around the menu
         *
         * Emits:
         *  modelValue: indicates that the open state of the menu has changed
         *
         *  <DropdownMenu type="button" button-class="btn-primary">
         *    <template #button>Options</template>
         *    <template #menu>
         *      <button @click="handleEdit">Edit</button>
         *      <DropdownMenu type="flyout">
         *        <template #button>
         *          Flyout Button Label
         *        </template>
         *        <template #menu>
         *          <button type="button" class="rec-dropdown-menu__item">
         *            Flyout Menu Button Label
         *          </button>
         *        </template>
         *      </DropdownMenu>
         *    </template>
         *  </DropdownMenu>
         *********************************************/
        //Scripts
        const props = __props;
        //Emits
        const emit = __emit;
        const model = useVModel(props, "modelValue", emit, {
            defaultValue: false,
            passive: true
        });
        const key = uuid();
        const dropdown = ref(null);
        const menu = ref();
        const menuButton = ref(null);
        const isButton = computed(() => props.type == "button");
        const isFlyout = computed(() => props.type == "flyout");
        const isSelect = computed(() => props.type == "select");
        const isFilterChip = computed(() => props.type == "filter-chip");
        const menuHeight = ref(600);
        const lockedPosition = ref(null);
        const classes = computed(() => ({
            "rec-dropdown": true,
            "rec-dropdown--open": model.value,
            "rec-dropdown--absolute": props.absolute,
            "rec-dropdown--with-icons": props.prependIcon || props.appendIcon,
            "rec-dropdown--with-icons-inside": props.prependIconInside || props.appendIconInside
        }));
        const buttonClasses = computed(() => {
            const classes = [];
            const builtin = isFlyout.value
                ? {
                    "rec-dropdown-menu__item rec-dropdown-menu__item--flyout": true,
                    "rec-dropdown-menu__item--active": model.value
                }
                : {
                    "rec-dropdown__button": true,
                    "rec-dropdown__button--has-tooltip": !!props.tooltip,
                    [`rec-dropdown__button--${props.type}`]: true,
                    "rec-dropdown__button--active": props.active, // Active state (e.g. values are selected in a RecSelect component)
                    "rec-dropdown__button--disabled": props.disabled,
                    btn: isButton.value,
                    "form-control": isSelect.value,
                    "rec-filter-chip": isFilterChip.value
                };
            classes.push(builtin);
            if (props.buttonClass)
                classes.push(props.buttonClass);
            return classes;
        });
        const menuClasses = computed(() => ["rec-dropdown-menu", props.menuClass || ""]);
        onClickOutside(menu, () => {
            if (!model.value)
                return;
            handleToggleMenuOpen(false);
        }, {
            ignore: [menuButton, ...props.ignoreClickOutside]
        });
        const handleToggleMenuOpen = (state) => {
            if (props.buttonOnly) {
                emit("click");
                return;
            }
            model.value = state !== undefined ? state : !model.value;
        };
        const onMenuClick = () => {
            if (!props.autoClose)
                return;
            handleToggleMenuOpen(false);
        };
        const menuStyle = ref({
            top: undefined,
            bottom: undefined,
            left: undefined,
            right: undefined,
            maxHeight: undefined,
            width: undefined,
            maxWidth: undefined
        });
        onMounted(() => {
            if (props.menuMaxHeight > 0) {
                menuStyle.value.maxHeight = toCssSizeUnit(props.menuMaxHeight);
            }
        });
        const positionMenu = () => {
            if (!menu.value || !menuButton.value)
                return;
            // Use locked position if available, otherwise get current position
            let buttonX, buttonWidth, buttonHeight, buttonRight, buttonTop, buttonBottom;
            if (props.lockPositionOnOpen && lockedPosition.value && model.value) {
                // Use locked position
                ({ buttonX, buttonWidth, buttonHeight, buttonRight, buttonTop, buttonBottom } = lockedPosition.value);
            }
            else {
                // Get current position and optionally lock it
                const bounds = useElementBounding(menuButton.value);
                buttonX = bounds.x.value;
                buttonWidth = bounds.width.value;
                buttonHeight = bounds.height.value;
                buttonRight = bounds.right.value;
                buttonTop = bounds.top.value;
                buttonBottom = bounds.bottom.value;
                // Lock position when dropdown opens
                if (props.lockPositionOnOpen && model.value && !lockedPosition.value) {
                    lockedPosition.value = {
                        buttonX,
                        buttonWidth,
                        buttonHeight,
                        buttonRight,
                        buttonTop,
                        buttonBottom
                    };
                }
            }
            const { width: menuWidth, height: menuHeight } = useElementBounding(menu);
            const { width: windowWidth, height: windowHeight } = useWindowSize({
                includeScrollbar: false
            });
            if (props.menuWidth > 0) {
                menuStyle.value.width = toCssSizeUnit(props.menuWidth);
                menuStyle.value.maxWidth = toCssSizeUnit(props.menuWidth);
            }
            else if (isSelect.value || (props.menuWidthMatchButton && !!buttonWidth)) {
                menuStyle.value.width = toCssSizeUnit(buttonWidth);
                menuStyle.value.maxWidth = toCssSizeUnit(buttonWidth);
            }
            // Menu spacing tweaks
            let offsetTop = isFlyout.value ? -buttonHeight - 12 : props.offsetY;
            let x = (isFlyout.value ? buttonRight : buttonX) + window.scrollX;
            let y = buttonBottom + offsetTop;
            if (props.absolute) {
                // menu absolute positioning
                y = buttonHeight + props.offsetY;
            }
            const parentTopAlignment = isFlyout.value && props.align === "parent-top";
            if (parentTopAlignment) {
                const parentMenu = menuButton.value?.closest(".rec-dropdown-menu[role=menu]");
                if (parentMenu) {
                    const { top } = parentMenu.getBoundingClientRect();
                    y = top;
                }
            }
            // Horizontal positioning
            if (x + menuWidth.value > windowWidth.value || props.menuPosition?.includes("left")) {
                // Menu cannot fit right aligned with button in viewport
                if (!props.absolute && buttonX + (isFlyout.value ? 0 : buttonWidth) - menuWidth.value < 0) {
                    // Menu cannot fit left or right aligned with button in viewport
                    y = buttonBottom + (isFlyout.value ? 0 : offsetTop);
                    menuStyle.value.right = "";
                    menuStyle.value.left = toCssSizeUnit(props.windowPadding);
                    if (isFlyout.value) {
                        offsetTop = buttonHeight;
                    }
                }
                else {
                    // Menu left aligned with button
                    menuStyle.value.left = "";
                    if (props.absolute) {
                        menuStyle.value.right = toCssSizeUnit(buttonX + buttonWidth - windowWidth.value);
                    }
                    else {
                        x = windowWidth.value - (isFlyout.value ? buttonX : buttonRight);
                        menuStyle.value.right = toCssSizeUnit(x);
                    }
                }
            }
            else {
                // Menu right aligned with button
                menuStyle.value.right = "";
                if (props.absolute) {
                    menuStyle.value.left = "0";
                }
                else {
                    menuStyle.value.left = toCssSizeUnit(x);
                }
            }
            // Vertical positioning
            let shouldPositionAbove = false;
            if (props.placement === "above") {
                shouldPositionAbove = true;
            }
            else if (props.placement === "below") {
                shouldPositionAbove = false;
            }
            else {
                // Auto placement - check if menu fits below button
                shouldPositionAbove = y + menuHeight.value > windowHeight.value - props.windowPadding;
            }
            if (shouldPositionAbove) {
                // Position above button
                menuStyle.value.top = toCssSizeUnit(Math.max(props.windowPadding, buttonTop - offsetTop - menuHeight.value));
                if (menuHeight.value > windowHeight.value - 2 * props.windowPadding) {
                    menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - 2 * props.windowPadding);
                }
            }
            else {
                // Position below button
                menuStyle.value.top = toCssSizeUnit(y);
                if (y + menuHeight.value > windowHeight.value - props.windowPadding) {
                    menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - y - props.windowPadding);
                }
            }
        };
        watchEffect(positionMenu);
        // Position menu when window size changes (should always reposition)
        const { width: windowWidth, height: windowHeight } = useWindowSize();
        watch([windowWidth, windowHeight], positionMenu);
        // Clear locked position when dropdown closes
        watch(model, (isOpen) => {
            if (!isOpen && props.lockPositionOnOpen) {
                lockedPosition.value = null;
            }
            // Remove focus from button when menu closes
            if (!isOpen && menuButton.value) {
                menuButton.value.blur();
            }
        });
        // Evaluate the style after an orientation change, with a delay to allow for animations to complete
        const { orientation } = useScreenOrientation();
        watch(orientation, () => {
            setTimeout(() => {
                if (!menu.value)
                    return;
                positionMenu();
            }, 500);
        });
        watchEffect((onCleanup) => {
            const eventListenerDisposer = useDropdownAccessibility({
                eventTargetEl: dropdown,
                menu: menu,
                isOpen: model,
                toggleSubMenu: handleToggleMenuOpen,
                menuToggleBtn: menuButton,
                submenu: isFlyout.value ? menu : undefined,
                menuItemBtnType: isFlyout.value ? MenuItemType.FLYOUT : MenuItemType.DROPDOWN
            });
            onCleanup(() => eventListenerDisposer());
        });
        __expose({
            menuButton,
            el: dropdown
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "dropdown",
                ref: dropdown,
                key: _unref(key),
                class: _normalizeClass(classes.value)
            }, [
                (_ctx.error)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "label label-danger rc-mb5",
                        textContent: _toDisplayString(_ctx.error)
                    }, null, 8, _hoisted_1))
                    : _createCommentVNode("", true),
                (_ctx.prependIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 1,
                        icon: _ctx.prependIcon,
                        src: _ctx.iconSrc,
                        class: "rec-dropdown__prepend-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                _withDirectives((_openBlock(), _createElementBlock("button", {
                    ref_key: "menuButton",
                    ref: menuButton,
                    class: _normalizeClass(buttonClasses.value),
                    disabled: !!_ctx.disabled,
                    "aria-haspopup": "true",
                    type: "button",
                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (handleToggleMenuOpen()), ["stop"]))
                }, [
                    (_ctx.prependIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 0,
                            icon: _ctx.prependIconInside,
                            src: _ctx.iconSrc,
                            class: "rec-dropdown__prepend-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    _renderSlot(_ctx.$slots, "button", _normalizeProps(_guardReactiveProps({ open: _unref(model) }))),
                    (_ctx.appendIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 1,
                            icon: _ctx.appendIconInside,
                            src: _ctx.iconSrc,
                            class: "rec-dropdown__append-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    (isFlyout.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", null, [
                                _createVNode(SvgIcon, {
                                    icon: "icon-arrow-right",
                                    class: "rec-flyout-indicator__chevron"
                                })
                            ])
                        ]))
                        : _createCommentVNode("", true)
                ], 10, _hoisted_2)), [
                    [_unref(vTooltip), _ctx.tooltip]
                ]),
                (_ctx.appendIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 2,
                        icon: _ctx.appendIcon,
                        src: _ctx.iconSrc,
                        class: "rec-dropdown__append-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                (_ctx.useVShow ? true : _unref(model) && _ctx.$slots.menu)
                    ? _withDirectives((_openBlock(), _createElementBlock("div", {
                        key: 3,
                        ref_key: "menu",
                        ref: menu,
                        class: _normalizeClass(menuClasses.value),
                        style: _normalizeStyle(menuStyle.value),
                        role: "menu",
                        onClick: _withModifiers(onMenuClick, ["stop"])
                    }, [
                        _renderSlot(_ctx.$slots, "menu", _normalizeProps(_guardReactiveProps({ toggleMenu: handleToggleMenuOpen, menuHeight: menuHeight.value })))
                    ], 6)), [
                        [_vShow, _ctx.useVShow ? _unref(model) && _ctx.$slots.menu : true]
                    ])
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
